import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, ShieldAlert, Tag, FileSearch, Users,
  Bell, FileBarChart, Settings, ScrollText, BookOpen, Wrench,
  ChevronLeft, ChevronRight, LogOut, Search, Globe, ListChecks, History, FileCode, Radio, Cpu, Smartphone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/common/Avatar';
import { ThemeSwitcher } from '@/components/common/ThemeSwitcher';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { useAuthStore } from '@/store/auth';
import { authApi } from '@/api/endpoints';
import { cn } from '@/lib/utils';

interface NavItem { to: string; label: string; icon: any; adminOnly?: boolean; }

export default function AppLayout() {
  const { t } = useTranslation();
  const { user, refreshToken, clearTokens, isAdmin } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  const items: NavItem[] = [
    { to: '/',            label: t('nav.dashboard'),    icon: LayoutDashboard },
    { to: '/live',        label: t('nav.live'),         icon: Radio },
    { to: '/detections',  label: t('nav.detections'),   icon: ShieldAlert },
    { to: '/catalog',     label: t('nav.catalog'),      icon: FileCode },
    { to: '/marks',       label: t('nav.marks'),        icon: Tag },
    { to: '/cases',       label: t('nav.cases'),        icon: FileSearch },
    { to: '/drill',       label: t('nav.drill'),        icon: Search },
    { to: '/hardware',    label: t('nav.hardware'),     icon: Cpu },
    { to: '/emulators',   label: t('nav.emulators'),    icon: Smartphone },
    { to: '/notifications', label: t('nav.notifications'), icon: Bell },
    { to: '/reports',     label: t('nav.reports'),      icon: FileBarChart },
    { to: '/changelog',   label: t('nav.changelog'),    icon: BookOpen },
    { to: '/audit',       label: t('nav.audit'),        icon: History, adminOnly: true },
    { to: '/users',       label: t('nav.users'),        icon: Users, adminOnly: true },
    { to: '/config',      label: t('nav.configGlobal'), icon: Wrench, adminOnly: true },
    { to: '/settings',    label: t('nav.settings'),     icon: Settings }
  ];

  async function handleLogout() {
    try { if (refreshToken) await authApi.logout(refreshToken); } catch { /* offline ok */ }
    clearTokens();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className={cn(
        'fixed inset-y-0 left-0 z-30 flex flex-col border-r bg-card transition-all',
        collapsed ? 'w-16' : 'w-64'
      )}>
        <div className="flex items-center gap-3 px-3 h-14 border-b">
          <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center shrink-0">
            <span className="text-primary-foreground font-bold text-lg">C</span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">CodeShield</div>
              <div className="text-xs text-muted-foreground truncate">Portal</div>
            </div>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {items.filter(i => !i.adminOnly || isAdmin()).map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => cn(
                'flex items-center gap-3 mx-2 my-0.5 px-3 py-2 rounded-md text-sm transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                isActive && 'bg-accent text-accent-foreground font-medium',
                collapsed && 'justify-center px-2'
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="border-t p-2">
          <Button variant="ghost" size="sm" onClick={() => setCollapsed(!collapsed)} className="w-full justify-center">
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className={cn('flex-1 flex flex-col transition-all', collapsed ? 'ml-16' : 'ml-64')}>
        <header className="h-14 border-b bg-card/80 backdrop-blur sticky top-0 z-20 flex items-center justify-end gap-2 px-4">
          <LanguageSwitcher />
          <ThemeSwitcher />
          <div className="flex items-center gap-2 ml-2 pl-3 border-l">
            <Avatar name={user?.displayName ?? user?.email ?? '?'} />
            <div className="text-sm">
              <div className="font-medium leading-none">{user?.displayName ?? '—'}</div>
              <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} title={t('auth.logout')}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
